import { Select as MuiSelect } from '@mui/material';
import { componentSizes } from '../../theme/componentSizes';
import { r, s } from '../../theme/tokens';
import { FormField } from './FormField';
import {
  resolveFormFieldBackgroundColor,
  type FormFieldBackgroundColor,
  type FormFieldStyleVariant,
} from './formFieldVariants';
import { overlayScrollbarSx } from './overlayScrollbarSx';
import type { SxProps, Theme } from './types';
import type { SelectProps as MuiSelectProps } from '@mui/material/Select';
import type { ReactElement, ReactNode } from 'react';

type SelectMenuSlotProps = NonNullable<NonNullable<MuiSelectProps['MenuProps']>['slotProps']>;
type SelectMenuPaperSlotProp = NonNullable<SelectMenuSlotProps['paper']>;
type SelectMenuListSlotProp = NonNullable<SelectMenuSlotProps['list']>;
type SlotPropOwnerState<TSlotProp> = TSlotProp extends (ownerState: infer TOwnerState) => unknown
  ? TOwnerState
  : never;
type SelectMenuPaperOwnerState = SlotPropOwnerState<SelectMenuPaperSlotProp>;
type SelectMenuListOwnerState = SlotPropOwnerState<SelectMenuListSlotProp>;

const toSxEntries = (sx: SxProps<Theme> | undefined): readonly unknown[] =>
  sx ? (Array.isArray(sx) ? sx : [sx]) : [];

export type SelectProps<TValue = unknown> = Omit<MuiSelectProps<TValue>, 'label'> & {
  label?: ReactNode;
  helperText?: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  fieldStyle?: FormFieldStyleVariant;
  inputBackgroundColor?: FormFieldBackgroundColor;
  inputHeight?: number;
};

export const Select = <TValue,>({
  label,
  helperText,
  helpText,
  errorText,
  fullWidth = true,
  size = 'small',
  fieldStyle = 'default',
  inputBackgroundColor,
  inputHeight,
  sx,
  error,
  required,
  MenuProps,
  ...rest
}: SelectProps<TValue>): ReactElement => {
  const resolvedInputBackgroundColor = resolveFormFieldBackgroundColor(
    fieldStyle,
    inputBackgroundColor,
  );
  const resolvedInputHeight = inputHeight ?? componentSizes.auth.inputHeight;
  const resolvedHelpText = helpText ?? (error ? undefined : helperText);
  const resolvedErrorText = errorText ?? (error ? helperText : undefined);
  const baseSx: MuiSelectProps<TValue>['sx'] = (theme) => ({
    '&.MuiInputBase-root, & .MuiInputBase-root': {
      backgroundColor: resolvedInputBackgroundColor,
      minHeight: `${resolvedInputHeight}px !important`,
      height: `${resolvedInputHeight}px !important`,
      maxHeight: `${resolvedInputHeight}px !important`,
      boxSizing: 'border-box',
    },
    '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
      backgroundColor: resolvedInputBackgroundColor,
      borderRadius: r('sm'),
      minHeight: `${resolvedInputHeight}px !important`,
      height: `${resolvedInputHeight}px !important`,
    },
    '& .MuiOutlinedInput-input': {
      height: '100%',
      boxSizing: 'border-box',
      paddingTop: s('none'),
      paddingBottom: s('none'),
      paddingLeft: s('md'),
      paddingRight: s('md'),
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      minHeight: `${resolvedInputHeight}px !important`,
      height: `${resolvedInputHeight}px !important`,
      boxSizing: 'border-box',
      paddingTop: s('none'),
      paddingBottom: s('none'),
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderRadius: r('sm') },
  });
  const mergedSx = [baseSx, sx].filter(Boolean) as MuiSelectProps<TValue>['sx'];
  const menuPaperSlotProps = MenuProps?.slotProps?.paper;
  const menuListSlotProps = MenuProps?.slotProps?.list;
  const mergedMenuProps: MuiSelectProps<TValue>['MenuProps'] = {
    ...MenuProps,
    variant: MenuProps?.variant ?? 'menu',
    anchorOrigin: MenuProps?.anchorOrigin ?? { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: MenuProps?.transformOrigin ?? { vertical: 'top', horizontal: 'left' },
    slotProps: {
      ...MenuProps?.slotProps,
      paper:
        typeof menuPaperSlotProps === 'function'
          ? (ownerState: SelectMenuPaperOwnerState) => {
              const resolvedPaperSlotProps = menuPaperSlotProps(ownerState);
              return {
                ...resolvedPaperSlotProps,
                square: false,
                sx: [
                  {
                    backgroundColor: 'var(--floating-control-surface)',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: `${r('md')} !important`,
                    boxShadow: 'none',
                    backgroundImage: 'none',
                    overflow: 'hidden !important',
                    '--Paper-overlay': 'none',
                    '--Paper-shadow': 'none',
                    '& .MuiMenuItem-root': {
                      fontSize: (theme: Theme) => theme.typography.body1.fontSize,
                      lineHeight: (theme: Theme) => theme.typography.body1.lineHeight,
                      fontWeight: (theme: Theme) => theme.typography.body1.fontWeight,
                    },
                    '& .MuiMenuItem-root .MuiTypography-root, & .MuiMenuItem-root .MuiListItemText-primary':
                      {
                        fontSize: (theme: Theme) => theme.typography.body1.fontSize,
                        lineHeight: (theme: Theme) => theme.typography.body1.lineHeight,
                        fontWeight: (theme: Theme) => theme.typography.body1.fontWeight,
                      },
                  },
                  ...toSxEntries(resolvedPaperSlotProps.sx),
                ] as SxProps<Theme>,
              } as Exclude<SelectMenuSlotProps['paper'], undefined>;
            }
          : ({
              ...menuPaperSlotProps,
              square: false,
              sx: [
                {
                  backgroundColor: 'var(--floating-control-surface)',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: `${r('md')} !important`,
                  boxShadow: 'none',
                  backgroundImage: 'none',
                  overflow: 'hidden !important',
                  '--Paper-overlay': 'none',
                  '--Paper-shadow': 'none',
                  '& .MuiMenuItem-root': {
                    fontSize: (theme: Theme) => theme.typography.body1.fontSize,
                    lineHeight: (theme: Theme) => theme.typography.body1.lineHeight,
                    fontWeight: (theme: Theme) => theme.typography.body1.fontWeight,
                  },
                  '& .MuiMenuItem-root .MuiTypography-root, & .MuiMenuItem-root .MuiListItemText-primary':
                    {
                      fontSize: (theme: Theme) => theme.typography.body1.fontSize,
                      lineHeight: (theme: Theme) => theme.typography.body1.lineHeight,
                      fontWeight: (theme: Theme) => theme.typography.body1.fontWeight,
                    },
                },
                ...toSxEntries(menuPaperSlotProps?.sx),
              ] as SxProps<Theme>,
            } as Exclude<SelectMenuSlotProps['paper'], undefined>),
      list:
        typeof menuListSlotProps === 'function'
          ? (ownerState: SelectMenuListOwnerState) => {
              const resolvedListSlotProps = menuListSlotProps(ownerState);
              return {
                ...resolvedListSlotProps,
                sx: [
                  { p: s('none') },
                  overlayScrollbarSx,
                  ...toSxEntries(resolvedListSlotProps.sx),
                ] as SxProps<Theme>,
              } as Exclude<SelectMenuSlotProps['list'], undefined>;
            }
          : ({
              ...menuListSlotProps,
              sx: [
                { p: s('none') },
                overlayScrollbarSx,
                ...toSxEntries(menuListSlotProps?.sx),
              ] as SxProps<Theme>,
            } as Exclude<SelectMenuSlotProps['list'], undefined>),
    },
  };

  return (
    <FormField
      label={label}
      helpText={resolvedHelpText}
      errorText={resolvedErrorText}
      required={required}
    >
      {(props) => {
        const { label: _label, ...fieldProps } = props;
        return (
          <MuiSelect
            {...rest}
            {...fieldProps}
            fullWidth={fullWidth}
            error={error}
            required={required}
            size={size}
            label={undefined}
            notched={false}
            MenuProps={mergedMenuProps}
            sx={mergedSx}
          />
        );
      }}
    </FormField>
  );
};
